import React, { useMemo } from "react";

import { useTranslation } from "utils";

import { Flex, Icon, Typography } from "components";

export type GuidanceBannerVariant = "welcome" | "progress" | "last" | "upload" | "complete";

export type GuidanceBannerProps = {
  availableYears: number[];
  completedYears: number[];
  hasOpenFileUploadTasks: boolean;
  selectedYear?: number;
  onSelectYear?: (year: number) => void;
  onShowVideo?: () => void;
  onNavigateToFileUpload?: () => void;
  onSaveAndExit?: () => void;
  className?: string;
};

const BANNER_ICON: Record<GuidanceBannerVariant, string> = {
  welcome: "tabler/bulb",
  progress: "tabler/confetti",
  last: "tabler/hand-finger",
  upload: "tabler/confetti",
  complete: "tabler/confetti",
};

type GuidanceBannerMessageParams = { variant: GuidanceBannerVariant; completedYear?: number; incompleteYears: number[] };

const BANNER_MESSAGES: Record<
  GuidanceBannerVariant,
  {
    titleKey: string;
    messageKey?: string;
    getParams?: (props: GuidanceBannerMessageParams) => Record<string, number | string>;
    actionKey?: string;
    displayArrow?: boolean;
  }
> = {
  // All years have missing data
  welcome: {
    titleKey: "guidanceBanner.welcome",
    messageKey: "guidanceBanner.welcomeMessage",
    actionKey: "guidanceBanner.welcomeAction",
  },
  // Year completed
  progress: {
    titleKey: "guidanceBanner.progress",
    messageKey: "guidanceBanner.progressMessage",
    getParams: ({ completedYear, incompleteYears }) => ({ year: completedYear ?? 0, nextYear: incompleteYears?.[0] ?? "" }),
    actionKey: "guidanceBanner.progressAction",
    displayArrow: true,
  },
  // Only one year has missing data
  last: {
    titleKey: "guidanceBanner.last",
    messageKey: "guidanceBanner.lastMessage",
    getParams: ({ incompleteYears }) => ({ year: incompleteYears?.[0] ?? "" }),
  },
  // All years completed and at least one file upload task is open
  upload: {
    titleKey: "guidanceBanner.complete",
    messageKey: "guidanceBanner.uploadMessage",
    actionKey: "guidanceBanner.uploadAction",
    displayArrow: true,
  },
  // All years completed and no file upload tasks are open
  complete: {
    titleKey: "guidanceBanner.complete",
    actionKey: "guidanceBanner.completeAction",
  },
};

export const GuidanceBanner = ({
  availableYears,
  completedYears,
  hasOpenFileUploadTasks,
  selectedYear,
  onSelectYear,
  onShowVideo,
  onNavigateToFileUpload,
  onSaveAndExit,
}: GuidanceBannerProps) => {
  const { t } = useTranslation("historicalPracticeDataTableView");

  const { variant, completedYear, incompleteYears } = useMemo(() => {
    const last = availableYears.filter((y) => !completedYears.includes(y));
    if (last.length === 0 && availableYears.length > 0 && hasOpenFileUploadTasks) return { variant: "upload" as const, incompleteYears: [] as number[] };
    if (last.length === 0 && availableYears.length > 0 && !hasOpenFileUploadTasks) return { variant: "complete" as const, incompleteYears: [] as number[] };
    if (completedYears.length === 0) return { variant: "welcome" as const, incompleteYears: [] as number[] };
    if (last.length === 1) return { variant: "last" as const, incompleteYears: last };
    if (selectedYear && completedYears.includes(selectedYear)) return { variant: "progress" as const, completedYear: selectedYear, incompleteYears: last };
    return { incompleteYears: last };
  }, [availableYears, completedYears, selectedYear, hasOpenFileUploadTasks]);

  if (!variant) return null;

  const { titleKey, messageKey, getParams, actionKey, displayArrow } = BANNER_MESSAGES[variant];
  const messageParams = getParams?.({ variant, completedYear, incompleteYears });

  const handleBannerActionClick = () => {
    if (variant === "welcome") {
      onShowVideo?.();
    }
    if (variant === "progress" && incompleteYears?.[0]) {
      onSelectYear?.(incompleteYears?.[0]);
    }
    if (variant === "upload") {
      onNavigateToFileUpload?.();
    }
    if (variant === "complete") {
      onSaveAndExit?.();
    }
  };

  const hasAction = Boolean(actionKey);

  return (
    <Flex align="center" justify="space-between" className={`historical-practice-data-table-view-banner-container ${variant}`}>
      <Flex gap="0.5rem" className="historical-practice-data-table-view-banner-message">
        <Icon iconName={BANNER_ICON[variant]} size={24} />
        <Flex gap="0.25rem">
          <Typography variant="paragraph-m" className="no-wrap" bold>
            {t(titleKey)}
          </Typography>
          {messageKey && <Typography variant="paragraph-m">{t(messageKey, messageParams)}</Typography>}
        </Flex>
      </Flex>
      {hasAction && (
        <Flex onClick={handleBannerActionClick} className={"historical-practice-data-table-view-banner-action"} gap="0.25rem" align="center">
          <Typography variant="paragraph-m" bold underline>
            {t(actionKey!, messageParams)}
          </Typography>
          {displayArrow && <Icon iconName="tabler/arrow-right" size={24} />}
        </Flex>
      )}
    </Flex>
  );
};
